import { FC } from 'react'
import { SingleColumnLayout } from 'components/layouts/Layouts'
import { PageHeading } from 'components/atoms/Text'
import ModuleWrapper from 'components/features/pfm/ModuleWrapper/ModuleWrapper'
import Anchor from 'components/atoms/navigation/Anchor'
import Button from 'components/features/pfm/Button/Button'
import AccountSettingsModuleContainer from 'components/features/pfm/AccountSettingsModule/AccountSettingsModuleContainer'
import ProfileSettingsModuleContainer from 'components/features/pfm/ProfileSettingsModule/ProfileSettingsModuleContainer'
import ContactSettingsModuleContainer from 'components/features/pfm/ContactSettingsModule/ContactSettingsModuleContainer'
import { HomeSettingsModule } from 'components/features/pfm/HomeSettingsModule/HomeSettingsModule'
import { CarSettingsModule } from 'components/features/pfm/CarSettingsModule/CarSettingsModule'
import DeviceSettingsModuleContainer from 'components/features/pfm/DeviceSettingsModule/DeviceSettingsModuleContainer'
import DeleteAccountModuleContainer from 'components/features/pfm/DeleteAccountModule/DeleteAccountModuleContainer'
import CommonFooter from 'components/molecules/CommonFooter'
import * as styles from 'components/features/pfm/SettingsPage/SettingsPage.css'
import MixedAccountConfirmModalContainer from 'components/features/pfm/ConfirmModal/MixedAccountConfirmModalContainer'
import BalanceAccountsSettingsModuleContainer from 'components/features/pfm/BalanceAccountSettingsModule/BalanceAccountsSettingsModuleContainer'
import InternalLayoutContainer from 'components/features/pfm/InternalLayout/InternalLayoutContainer'
import InternalSidebarContainer from 'components/features/pfm/InternalSidebar/InternalSidebarContainer'
import { NotificationTrayWrapper } from 'components/features/pfm/NotificationTray/NotificationTrayWrapper'
import { SubscriptionSettingsModule } from 'components/features/pfm/SubscriptionSettingsModule/SubscriptionSettingsModule'
import { LinkAccountHandler } from 'components/features/pfm/LinkAccountHandler/LinkAccountHandler'
import { HouseEditHandler } from 'components/features/pfm/HouseEditHandler/HouseEditHandler'
import { CarEditHandler } from 'components/features/pfm/CarEditHandler/CarEditHandler'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { ccStateGuard } from 'components/features/creditCard/creditCardStore/guards/ccStateGuard'
import { EMPTY_OBJ } from 'constants/component'

const SettingsPage: FC<{}> = () => {

  useSimpleGuard(ccStateGuard, EMPTY_OBJ, 'CreditCardPageAuth')

  const accountModule = (
    <ModuleWrapper qaType='module-wrapper' qaMarker='status-account' title='Your Status Account' corners='all' forceRoundedCorners>
      <AccountSettingsModuleContainer />
    </ModuleWrapper>
  )

  const profileModule = (
    <ModuleWrapper qaType='module-wrapper' qaMarker='profile' title='Your Profile' corners='all' forceRoundedCorners>
      <ProfileSettingsModuleContainer />
    </ModuleWrapper>
  )

  const contactsModule = (
    <ModuleWrapper qaType='module-wrapper' qaMarker='contact-information' title='Your Contact Information' corners='all' forceRoundedCorners>
      <ContactSettingsModuleContainer />
    </ModuleWrapper>
  )

  const subscriptionModule = (
    <ModuleWrapper
      title='Your Membership'
      card
      anchor='settings_subscription'
      qaType='module-wrapper'
      qaMarker='membership'
      corners='all'
      forceRoundedCorners
    >
      <SubscriptionSettingsModule />
    </ModuleWrapper>
  )

  const linkNewAccountButton = (
    <LinkAccountHandler>
      <Button
        size='tiny'
        style='responsiveTextFilled'
        qaMarker='link-new-account-button'
      >
        Link New Account
      </Button>
    </LinkAccountHandler>
  )

  const linkNewHomeButton = (
    <HouseEditHandler>
      <Button
        style='responsiveTextFilled'
        size='tiny'
        qaMarker='link-new-home-button'
      >
        Link New Home
      </Button>
    </HouseEditHandler>
  )

  const linkNewCarButton = (
    <CarEditHandler>
      <Button
        style='responsiveTextFilled'
        size='tiny'
        qaMarker='link-new-car-button'
      >
        Link New Car
      </Button>
    </CarEditHandler>
  )

  const notifications = (
    <NotificationTrayWrapper
      renderLevel='page'
      isPositionRelative
    />
  )

  const pageBody = (
    <>
      <div className={ styles.personalInfoGrid }>
        <div className={ styles.profileModuleCell }>
          { profileModule }
        </div>
        <div className={ styles.accountModuleCell }>
          { accountModule }
        </div>
        <div className={ styles.subscriptionModuleCell }>
          { subscriptionModule }
        </div>
        <div className={ styles.contactModuleCell }>
          { contactsModule }
        </div>
      </div>

      <ModuleWrapper
        title='Your Accounts'
        card={ false }
        action={ linkNewAccountButton }
        anchor='settings_accounts'
        qaType='module-wrapper'
        qaMarker='accounts'
        corners='all'
        forceRoundedCorners
      >
        <BalanceAccountsSettingsModuleContainer />
      </ModuleWrapper>

      <ModuleWrapper
        title='Your Homes'
        card={ false }
        action={ linkNewHomeButton }
        anchor='settings_homes'
        qaType='module-wrapper'
        qaMarker='homes'
        corners='all'
        forceRoundedCorners
      >
        <HomeSettingsModule />
      </ModuleWrapper>

      <ModuleWrapper
        title='Your Cars'
        card={ false }
        action={ linkNewCarButton }
        anchor='settings_cars'
        qaType='module-wrapper'
        qaMarker='cars'
        corners='all'
        forceRoundedCorners
      >
        <CarSettingsModule />
      </ModuleWrapper>

      <ModuleWrapper
        title='Verified Devices'
        card={ false }
        qaType='module-wrapper'
        qaMarker='verified-devices'
        corners='all'
        forceRoundedCorners
      >
        <DeviceSettingsModuleContainer />
      </ModuleWrapper>

      <MixedAccountConfirmModalContainer />
      <DeleteAccountModuleContainer />
    </>
  )

  const header = (
    <PageHeading>Settings</PageHeading>
  )

  return (
    <InternalLayoutContainer
      sidebar={ <InternalSidebarContainer /> }
      notifications={ notifications }
    >
      <Anchor placement='settings'>
        <SingleColumnLayout header={ header }>
          { pageBody }
          <div className={ styles.version }>ver: { __VERSION__ }</div>
          <div className={ styles.footerWrapper }>
            <CommonFooter />
          </div>
        </SingleColumnLayout>
      </Anchor>
    </InternalLayoutContainer>
  )
}

export default SettingsPage
